import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  AppGoalCenterWrapper,
  AppGoalCenterHeading,
  AppGoalCenterSubHeading,
  AppCountCardWrapper,
  AppCard,
  AppContent,
  AppCountWrapper,
  AppCountBubble,
  AppCount,
  Divider,
  AppContentLabel,
} from 'components/AppGoalCenter/AppGoalCenter.style'
import GradientCircleNotchIcon from 'components/Common/SvgIcons/GradientCircleNotchIcon'
import { NewInsightsPopup } from 'components/NewInsightsPopup/NewInsightsPopup.component'
import { useNewInsightsPopup } from 'components/NewInsightsPopup/NewInsightsPopup.hook'

import {
  BusinessGoalsFilterData,
  UserTaskItemStatus,
  useGetBusinessGoalsQuery,
  useGetPlayCountMetricsQuery,
  useGetTouchdownCountMetricsQuery,
} from '__generated__/api-types-and-hooks'
import { getTenantId } from 'utils/getTenantId'

import { BusinessGoal } from '__generated__/api-types-and-hooks'
import { RootState } from 'App'
import AppSidebar from './AppSidebar/AppSidebar.component'
import { AppGoals } from './AppGoals/AppGoals.component'
import { myOtherTasksGoal } from 'appConfig/data'
import { isAdvisorRole } from 'utils/helper'
import { GOALIDS, OTHERTASKS } from 'appConfig/enums'
import moment from 'moment'

interface IAppGoalCenterProps {
  businessName?: string
}

const AppGoalCenter: React.FC<IAppGoalCenterProps> = ({ businessName }) => {
  const { t } = useTranslation()
  // const clientId = useSelector((state: RootState) => {
  //   return state.clients.client.profile.id
  // })
  const { clientId } = useParams()
  const userId = useSelector((state: RootState) => {
    return state.user.user.id
  })
  const tenantId = getTenantId()

  // New Insights Popup
  const { showPopup, insightsCount, handleClose, handleViewInsights } = useNewInsightsPopup()
  const [selectedFilters, setSelectedFilters] = useState<BusinessGoalsFilterData>({
    isArchive: false,
  })

  const [viewArchive, setViewArchive] = useState<boolean>(false)

  const { data, isLoading, refetch } = useGetBusinessGoalsQuery(
    {
      input: { userId: clientId ?? userId, tenantId, filter: selectedFilters },
    },
    { refetchOnWindowFocus: false }
  )

  let updatedGoals = data?.getBusinessGoals
    ? [...myOtherTasksGoal, ...data.getBusinessGoals]
    : [...myOtherTasksGoal]

  useEffect(() => {
    if (updatedGoals) {
      const allPlaysGoal = updatedGoals.find((goal) =>
        [OTHERTASKS.MY_ALL_PLAYS as string, t('playboard.otherTasks.allPlays')].includes(
          goal.name as string
        )
      )
      if (allPlaysGoal) {
        allPlaysGoal.name = t('playboard.otherTasks.allPlays')
        allPlaysGoal.description =
          isAdvisorRole() && !clientId
            ? t('playboard.otherTasks.allPlaysAdvisorDesc')
            : t('playboard.otherTasks.allPlaysDesc')
      }

      const miscPlaysGoal = updatedGoals.find((goal) => goal.goalId === GOALIDS.MY_OTHER_TASKS)
      if (miscPlaysGoal) {
        miscPlaysGoal.name =
          isAdvisorRole() && !clientId
            ? t('playboard.otherTasks.myMiscellaneousPlays')
            : t('playboard.otherTasks.miscellaneousPlays')
        miscPlaysGoal.description = t('playboard.otherTasks.miscellaneousPlaysDesc')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, t])

  const { data: allGoals, refetch: refetchBusinessGoals } = useGetBusinessGoalsQuery(
    {
      input: { userId: clientId ?? userId, tenantId },
    },
    { refetchOnWindowFocus: false }
  )

  const completedGoals = useMemo(
    () => allGoals?.getBusinessGoals?.filter((goal) => goal.status === UserTaskItemStatus.Done),
    [allGoals]
  )

  const { data: touchdownsCompletedMetric } = useGetTouchdownCountMetricsQuery(
    {
      asOfDate: moment().format('YYYY-MM-DD'),
      targetUserId: clientId ?? userId,
    },
    { refetchOnWindowFocus: false }
  )

  const completedTouchdowns = useMemo(() => {
    return touchdownsCompletedMetric?.tdCount?.counter?.value ?? 0
  }, [touchdownsCompletedMetric?.tdCount?.counter?.value])

  const { data: playsCompletedMetric } = useGetPlayCountMetricsQuery(
    {
      asOfDate: moment().format('YYYY-MM-DD'),
      targetUserId: clientId ?? userId,
    },
    { refetchOnWindowFocus: false }
  )

  const completedPlays = useMemo(() => {
    return playsCompletedMetric?.playCount?.counter?.value ?? 0
  }, [playsCompletedMetric?.playCount?.counter?.value])

  const handleRefetchBusinessGoals = ({ hasFilter }: { hasFilter?: boolean }) => {
    hasFilter ? refetch() : refetchBusinessGoals()
  }
  return (
    <div className="flex flex-col sm:flex-row gap-y-4 w-full">
      {!isLoading && (
        <AppSidebar setSelectedFilters={setSelectedFilters} selectedFilters={selectedFilters} />
      )}
      <div className="flex-1 flex flex-col gap-8 py-7 xs:px-5 sm:px-6 md:px-12 lg:px-12 xl:px-24">
        {!isLoading && (
          <>
            <AppGoalCenterWrapper>
              <AppGoalCenterHeading>
                {!clientId
                  ? t('goalCenter.welcomeTitle')
                  : businessName
                  ? t('goalCenter.clientGoalCenter', { businessName })
                  : t('goalCenter.defaultGoalCenter')}
              </AppGoalCenterHeading>
              <AppGoalCenterSubHeading>
                {clientId ? t('goalCenter.clientSubtitle') : t('goalCenter.subtitle')}
              </AppGoalCenterSubHeading>
            </AppGoalCenterWrapper>

            <AppCountCardWrapper>
              <AppCard>
                <AppContent>
                  <AppContentLabel>{t('goalCenter.touchdownsCompleted')}</AppContentLabel>
                </AppContent>
                <AppCountWrapper>
                  <AppCountBubble>
                    <AppCount>{completedTouchdowns}</AppCount>
                  </AppCountBubble>
                </AppCountWrapper>
              </AppCard>

              {!(isAdvisorRole() && !clientId) && (
                <AppCard>
                  <AppContent>
                    <AppContentLabel>{t('goalCenter.goalsCompleted')}</AppContentLabel>
                  </AppContent>
                  <AppCountWrapper>
                    <AppCountBubble>
                      <AppCount>{completedGoals ? completedGoals.length : 0}</AppCount>
                    </AppCountBubble>
                  </AppCountWrapper>
                </AppCard>
              )}

              {isAdvisorRole() && !clientId && (
                <AppCard>
                  <AppContent>
                    <AppContentLabel>{t('goalCenter.playsCompleted')}</AppContentLabel>
                  </AppContent>
                  <AppCountWrapper>
                    <AppCountBubble>
                      <AppCount>{completedPlays}</AppCount>
                    </AppCountBubble>
                  </AppCountWrapper>
                </AppCard>
              )}
            </AppCountCardWrapper>

            <Divider />
          </>
        )}

        <div className="">
          {isLoading ? (
            <div className="flex justify-center items-center">
              <GradientCircleNotchIcon width="100" height="100" />
            </div>
          ) : (
            <AppGoals
              goals={updatedGoals}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              refetchGoals={handleRefetchBusinessGoals}
              viewArchive={viewArchive}
              setViewArchive={setViewArchive}
              allPlays={allGoals?.getBusinessGoals as BusinessGoal[]}
            />
          )}
        </div>
      </div>
      {showPopup && (
        <NewInsightsPopup
          onClose={handleClose}
          onViewInsights={handleViewInsights}
          insightsCount={insightsCount}
        />
      )}
    </div>
  )
}

export default AppGoalCenter
